import React, { useCallback, useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import IconBack from "app/pages/.shared/static/icons/IconBack";
import "./SearchMyBooking.scss";
import Button from "app/pages/.shared/form/Button";
import { Form, Formik } from "formik";
import isEmpty from "lodash/isEmpty";
import InputFormik from "app/pages/.shared/form/InputFormik";
import {
	defaultValues,
	validationSchema,
} from "app/pages/Account/SearchMyBooking/SearchMyBookingFormSchema";
import SignupMenuAccount from "app/pages/.shared/MenuAccount/SignupMenuAccount/SignupMenuAccount";
import { FlashSaleMenuAccountContainer } from "app/pages/.shared/MenuAccount/FlashSaleMenuAccount/FlashSaleMenuAccountContainer";
import MyBookingItem from "app/pages/Account/MyBookings/MyBookingItem/MyBookingItem";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import HeaderContainer from "app/pages/Header/HeaderContainer";
import RelativeLink from "app/pages/.shared/RelativeLink";
import PropTypes from "prop-types";
import Popover from "app/pages/.shared/Popover";
import validationMessages from "app/utils/form/validationMessages";
import { SEARCH_MY_BOOKINGS_STATUS } from "app/constants";
import RecoverBookingDataContainer from "app/pages/CorporateSite/RecoverBookingData/RecoverBookingDataContainer";
import IconCrossDark from "app/pages/.shared/static/icons/IconCrossDark";

const SearchMyBooking = ({ onFormSuccess, searchMyBooking, isWhiteLabel, userIsConnected }) => {
	const { resolution } = useContext(AppGlobalsContext);
	const [myBookingNotFound, setMyBookingNotFound] = useState(false);
	const [showBookingForm, setShowBookingForm] = useState(true);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const handleSubmit = useCallback((values, actions) => {
		actions.setSubmitting(false);
		onFormSuccess(values.booking, values.email);
	}, []);

	const onClickCross = useCallback(() => {
		setShowBookingForm(true);
	}, []);

	const handleRecoverLinkClick = useCallback(() => {
		setShowBookingForm(false);
	}, []);

	useEffect(() => {
		setMyBookingNotFound(searchMyBooking.status === SEARCH_MY_BOOKINGS_STATUS.NOT_FOUND);
	}, [searchMyBooking]);

	return searchMyBooking.booking ? (
		<div className="search-my-booking-find">
			{isMobile && <HeaderContainer />}
			<div className="search-my-booking-find__menu">
				{isWhiteLabel && !userIsConnected ? (
					<SignupMenuAccount />
				) : (
					<FlashSaleMenuAccountContainer />
				)}
			</div>
			<div className="search-my-booking-find__booking">
				<div className="search-my-booking-find__booking-title">
					<FormattedMessage id="my.bookings.title" />
				</div>
				<div className="search-my-booking-find__booking-subtitle">
					<FormattedMessage id="search.my.booking.subtitle" />
				</div>
				<MyBookingItem myBooking={searchMyBooking.booking} />
			</div>
		</div>
	) : (
		<div className="search-my-booking">
			<div className="search-my-booking__header">
				<div
					className="search-my-booking__header-back"
					onClick={() => {
						window.history.back();
					}}
				>
					<IconBack />
				</div>
				<div className="search-my-booking__header-title">
					<FormattedMessage id="search.my.booking.header" />
				</div>
				<div className="search-my-booking__header-right" />
			</div>
			{showBookingForm ? (
				<div className="search-my-booking__body">
					<header className="search-my-booking__headline">
						<FormattedMessage id="search.my.booking.headline" />
					</header>
					<Formik
						enableReinitialize
						initialValues={defaultValues}
						validateOnChange={false}
						validateOnBlur={false}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
					>
						{({ errors, isSubmitting, setFieldError, setErrors }) => {
							const getFormGlobalError = errors => {
								let _error = validationMessages.formGenericError;
								if (errors.booking) {
									_error = errors.booking;
								} else if (errors.email) {
									_error = errors.email;
								} else if (myBookingNotFound) {
									_error = { id: "error.booking.not.found.error" };
								}
								return _error;
							};

							const resetFormError = () => {
								setErrors({});
								setMyBookingNotFound(false);
							};

							return (
								<>
									<Form
										className="search-my-booking__form"
										onChange={event => {
											// Permet de supprimer l'état en erreur du champ en erreur lorsque le user commence à le  modifier
											// Actuellement pas possible de le faire facilement avec formik
											// https://github.com/formium/formik/issues/2727
											if (!isEmpty(errors)) {
												setFieldError(event.target.name);
											}
										}}
									>
										<InputFormik
											id="booking"
											data-cy="booking-reference-input"
											name="booking"
											type="text"
											label={
												<FormattedMessage id="search.my.booking.booking.input.label" />
											}
										/>

										<InputFormik
											id="email"
											data-cy="email-input"
											name="email"
											type="text"
											label={
												<FormattedMessage id="search.my.booking.email.input.label" />
											}
										/>

										<div
											className="search-my-booking__forgotten-infos"
											onClick={handleRecoverLinkClick}
										>
											<FormattedMessage id="search.my.booking.forgotten.label" />
										</div>

										<div className="search-my-booking__form-footer">
											<Button
												design="primary"
												submit
												className="search-my-booking__button"
												loading={isSubmitting}
												data-cy="search-button"
											>
												<FormattedMessage id="search.my.booking.cta.label" />
											</Button>

											<Popover
												isOpen={
													errors.booking ||
													errors.email ||
													myBookingNotFound
												}
												onClose={resetFormError}
											>
												{errors.booking ||
												errors.email ||
												myBookingNotFound ? (
													<FormattedMessage
														{...getFormGlobalError(errors)}
													/>
												) : (
													""
												)}
											</Popover>
										</div>
									</Form>
								</>
							);
						}}
					</Formik>
					<div className="search-my-booking__footer">
						<RelativeLink
							className="nav__item-link"
							to={{ pathname: "/booking-auth" }}
							data-cy="switch-to-booking-auth-link"
						>
							<FormattedMessage id="search.my.booking.footer.label" />
						</RelativeLink>
					</div>
				</div>
			) : (
				<div className="search-my-booking__recover">
					<div className="search-my-booking__form-recover">
						<div className="search-my-booking__cross" onClick={onClickCross}>
							<IconCrossDark />
						</div>
						<RecoverBookingDataContainer onRecoverSuccess={onClickCross} />
					</div>
				</div>
			)}
		</div>
	);
};

SearchMyBooking.propTypes = {
	onFormSuccess: PropTypes.func,
	searchMyBooking: PropTypes.object,
	isWhiteLabel: PropTypes.bool,
	userIsConnected: PropTypes.bool,
};

SearchMyBooking.defaultProps = {
	searchMyBooking: {},
};

export default React.memo(SearchMyBooking);
